import { z } from "zod";

import { properCase } from "@/src/lib/string";
import { DatabaseDefaults } from "@/src/models/database-defaults";

export const addressSchema = z.object({
  line1: z.string().nullable().describe(`
      Extract the Address Line 1 from this document. This field specifies the street address, which is the primary address line containing the street number and name. Address Line 1 should not include any apartment, unit, or suite information as those belong in Address Line 2.

      Here are some examples:

      123 Johnson St. Apt 200
      - Address Line 1= 123 Johnson St.

      567 Washington Ave. Unit 302
      - Address Line 1= 567 Washington Ave.

    `),
  line2: z.string().nullable().describe(`
      Extract the Address Line 2 from this document. This field specifies the Apartment, Unit, Floor, Lot, suite, Building number, etc. for a given address. It will always come AFTER the street address, which goes in Address Line 1. Address Line 1 should not be combined with Address Line 2.

      Here are some examples:

      123 Johnson St. Apt 200
      - Address Line 2= Apt 200

      567 Washington Ave. Unit 302

      - Address Line 2= Unit 302

      These are some examples of what the address line 2 field may look similar to:
      - Apt 100
      - 3A
      - Suite 200
      - STE 81
      - Building E
      - 105
      - Lot 51A
      - Unit B
      - Floor 3
    `),
  city: z.string().nullable(),
  district: z.string().nullable(),
  state: z.string().nullable(),
  postalCode: z.string().nullable(),
  country: z
    .string()
    .nullable()
    .describe(
      `
    The country of the address.
    If you cannot find the country, return "USA"
    `
    ),
});

export type Address = z.infer<typeof addressSchema> &
  DatabaseDefaults & {
    placeId: string | null;
    googlePlaceId: string | null;
    googleLine1: string | null;
    googleLine2: string | null;
    googleCity: string | null;
    googleDistrict: string | null;
    googleState: string | null;
    googlePostalCode: string | null;
    googleCountry: string | null;
  };

export const addressShape: Address = {
  id: "",
  line1: null,
  line2: null,
  city: null,
  district: null,
  state: null,
  postalCode: null,
  country: null,
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
  groupId: "",
  placeId: null,
  googlePlaceId: null,
  googleLine1: null,
  googleLine2: null,
  googleCity: null,
  googleDistrict: null,
  googleState: null,
  googlePostalCode: null,
  googleCountry: null,
};

export function getFormattedAddress(address: Address | null | undefined) {
  if (address == null) {
    return null;
  }

  const streetParts = [address.line1, address.line2].filter(Boolean).map((line) => properCase(line!));

  const stateZip = [address.state ? address.state.toUpperCase() : null, address.postalCode].filter(Boolean).join(" ");

  const cityStateZipParts = [address.city ? properCase(address.city) : null, stateZip].filter(Boolean).join(", ");

  const allParts = [...streetParts, cityStateZipParts].filter(Boolean);

  return allParts.length > 0 ? allParts.join(" ") : null;
}
